import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUploadField from "@/components/admin/ImageUploadField";

const EMPTY = { name: "", role: "", location: "", bio: "", image_url: "", setup: [], featured: false };
const inputCls = "h-11 w-full border border-black/20 bg-white px-3 text-sm outline-none focus:border-gavra";
const labelCls = "font-mono-hw text-[11px] uppercase tracking-widest text-black/50";

export default function AdminArtists() {
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [setupText, setSetupText] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [nameError, setNameError] = useState("");

  const load = () => api.get("/artists").then((r) => setArtists(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setSetupText(""); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({ ...EMPTY, ...a, setup: a.setup || [] });
    setSetupText((a.setup || []).join(", "));
    setOpen(true);
  };

  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) { setNameError("Artist name is required."); toast.error("Please fill in the required fields."); return; }
    setNameError("");
    setSaving(true);
    const payload = { ...form, setup: setupText.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editing) await api.put(`/artists/${editing}`, payload);
      else await api.post("/artists", payload);
      toast.success(editing ? "Artist updated" : "Artist created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/artists/${deleteId}`);
      toast.success("Artist deleted");
      setDeleteId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="admin-artists">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono-hw text-[11px] uppercase tracking-[0.25em] text-gavra">Roster</p>
          <h1 className="mt-2 font-heading text-4xl font-black uppercase tracking-tight md:text-5xl">Artists</h1>
        </div>
        <button data-testid="add-artist-btn" onClick={openNew} className="flex items-center gap-2 bg-black px-5 py-3 text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra">
          <Plus className="h-4 w-4" /> Add artist
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {artists.map((a) => (
          <div key={a.id} data-testid={`artist-row-${a.id}`} className="border border-black/10">
            <div className="aspect-[4/3] overflow-hidden border-b border-black/10 bg-secondary">
              {a.image_url && <img src={a.image_url} alt={a.name} className="h-full w-full object-cover" />}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading text-lg font-bold uppercase">{a.name}</h3>
                  <p className="font-mono-hw text-[10px] uppercase tracking-widest text-gavra">{a.role}</p>
                </div>
                {a.featured && <span className="bg-gavra px-2 py-0.5 text-[10px] font-bold uppercase text-white">Featured</span>}
              </div>
              <div className="mt-4 flex gap-2">
                <button data-testid={`edit-artist-${a.id}`} onClick={() => openEdit(a)} className="flex-1 border border-black/20 py-2 text-xs font-bold uppercase hover:border-gavra hover:text-gavra">
                  <Pencil className="mx-auto h-4 w-4" />
                </button>
                <button data-testid={`delete-artist-${a.id}`} onClick={() => setDeleteId(a.id)} className="flex-1 border border-black/20 py-2 text-xs font-bold uppercase hover:border-destructive hover:text-destructive">
                  <Trash2 className="mx-auto h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {artists.length === 0 && <p className="text-sm text-black/40">No artists yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-none border-black/10" data-testid="artist-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black uppercase tracking-tight">
              {editing ? "Edit artist" : "New artist"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input data-testid="af-name" value={form.name} onChange={(e) => { set("name")(e); setNameError(""); }} className={`mt-1.5 ${inputCls} ${nameError ? "border-destructive" : ""}`} />
              {nameError && <p data-testid="af-name-error" className="mt-1 text-xs text-destructive">{nameError}</p>}
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Role / Band</label>
                <input data-testid="af-role" value={form.role} onChange={set("role")} className={`mt-1.5 ${inputCls}`} />
              </div>
              <div>
                <label className={labelCls}>Location</label>
                <input data-testid="af-location" value={form.location} onChange={set("location")} className={`mt-1.5 ${inputCls}`} />
              </div>
            </div>
            <ImageUploadField label="Photo" testid="af-image" value={form.image_url} onChange={(v) => setForm((f) => ({ ...f, image_url: v }))} />
            <div>
              <label className={labelCls}>Bio</label>
              <textarea data-testid="af-bio" value={form.bio} onChange={set("bio")} rows={4} className="mt-1.5 w-full border border-black/20 bg-white p-3 text-sm outline-none focus:border-gavra" />
            </div>
            <div>
              <label className={labelCls}>Rig (comma separated)</label>
              <input data-testid="af-setup" value={setupText} onChange={(e) => setSetupText(e.target.value)} className={`mt-1.5 ${inputCls}`} placeholder="Apex Hoop, Drum Key" />
            </div>
            <label className="flex items-center gap-2 text-sm font-bold">
              <input data-testid="af-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="h-4 w-4 accent-[#FF5A00]" /> Featured on homepage
            </label>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="border border-black px-6 py-3 text-[12px] font-bold uppercase tracking-[0.15em] hover:bg-black hover:text-white">Cancel</button>
              <button data-testid="af-save" type="submit" disabled={saving} className="bg-black px-6 py-3 text-[12px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra disabled:opacity-50">
                {saving ? "Saving…" : "Save artist"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none border-black/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase">Delete artist?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-artist" onClick={confirmDelete} className="rounded-none bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
